import * as amplitude from "@amplitude/analytics-react-native";
import { Identify, Types } from "@amplitude/analytics-react-native";
import { amplitudeProject, environment } from "@src/config/runtime";
import { create } from "zustand";
import { isWeb } from "@src/utils/web-helpers";
import type { User } from "@src/features/auth/types";
import { withCachedUser } from "@src/features/auth/user-profile-merge";

export interface UserProperties {
  email?: string;
  name?: string;
  realm?: string;
  tenantId?: string;
  tenants?: string[];
  realms?: string[];
  role?: string;
  [key: string]: any;
}

export interface EventProperties {
  [key: string]: any;
}

interface AmplitudeState {
  isInitialized: boolean;
  setIsInitialized: (value: boolean) => void;
}

const useAmplitudeStore = create<AmplitudeState>()((set) => ({
  isInitialized: false,
  setIsInitialized: (value) => set({ isInitialized: value }),
}));

/**
 * Initialize Amplitude SDK (call once at app start)
 */
export const initAmplitude = async (): Promise<void> => {
  if (useAmplitudeStore.getState().isInitialized) {
    return;
  }

  const env = environment();
  const { apiKey } = amplitudeProject;

  try {
    await amplitude.init(apiKey, undefined, {
      disableCookies: true,
      serverZone: "EU", // GDPR compliance — data must stay in EU. Do not change without legal review.
      logLevel: Types.LogLevel.Error,
    }).promise;
    useAmplitudeStore.getState().setIsInitialized(true);
    console.log(`[Amplitude] Initialized for ${isWeb ? "web" : "mobile"} (${env.key})`);
  } catch (error) {
    console.error("[Amplitude] Init failed:", error);
  }
};

/**
 * Track an event
 */
export const trackEvent = (eventName: string, properties?: EventProperties): void => {
  if (!useAmplitudeStore.getState().isInitialized) {
    return;
  }

  try {
    amplitude.track(eventName, properties);
  } catch (error) {
    console.error("[Amplitude] Track failed:", eventName, error);
  }
};

/**
 * Identify a user with properties
 */
export const identifyUser = (userId: string, userProperties?: UserProperties): void => {
  if (!useAmplitudeStore.getState().isInitialized) {
    return;
  }

  try {
    amplitude.setUserId(userId);

    if (userProperties) {
      const identifyObj = new Identify();
      Object.entries(userProperties).forEach(([key, value]) => {
        identifyObj.set(key, value);
      });
      amplitude.identify(identifyObj);
    }
  } catch (error) {
    console.error("[Amplitude] Identify failed:", error);
  }
};

/**
 * Set a single user property
 */
export const setUserProperty = (key: string, value: any): void => {
  if (!useAmplitudeStore.getState().isInitialized) {
    return;
  }

  try {
    const identifyObj = new Identify();
    identifyObj.set(key, value);
    amplitude.identify(identifyObj);
  } catch (error) {
    console.error("[Amplitude] Set property failed:", key, error);
  }
};

/**
 * Clear user ID and properties (on logout)
 */
export const resetAmplitude = (): void => {
  if (!useAmplitudeStore.getState().isInitialized) {
    return;
  }

  try {
    amplitude.reset();
  } catch (error) {
    console.error("[Amplitude] Reset failed:", error);
  }
};

/**
 * Set user ID without properties
 */
export const setUserId = (userId: string | null): void => {
  if (!useAmplitudeStore.getState().isInitialized) {
    return;
  }

  try {
    amplitude.setUserId(userId ?? undefined);
  } catch (error) {
    console.error("[Amplitude] Set user ID failed:", error);
  }
};

/**
 * Check if Amplitude is initialized
 */
export const isAmplitudeInitialized = (): boolean => {
  return useAmplitudeStore.getState().isInitialized;
};

interface TenantContext {
  selectedTenant?: { id: string; realm: { fqdn: string } };
  tenants?: readonly { id: string; realm: { fqdn: string } }[];
}

/**
 * Identify current user with tenant context for feature flag targeting.
 * Sends realm, tenantId, and available tenants to Amplitude.
 *
 * @param user - The authenticated user
 * @param tenantContext - Optional tenant context. If not provided, only user info is sent.
 */
export const identifyCurrentUserWithAmplitude = (user: User, tenantContext?: TenantContext): void => {
  if (!isAmplitudeInitialized()) return;

  const profile = withCachedUser(user);
  const tenants = tenantContext?.tenants ?? [];
  const tenantIds = tenants.map((t) => t.id);
  const realms = [...new Set(tenants.map((t) => t.realm.fqdn))];
  const selectedTenant = tenantContext?.selectedTenant;

  identifyUser(profile.id, {
    ...(profile.email && { email: profile.email }),
    ...(profile.name && { name: profile.name }),
    ...(tenantContext && {
      ...(selectedTenant && { realm: selectedTenant.realm.fqdn, tenantId: selectedTenant.id }),
      ...(tenantIds.length > 0 && { tenants: tenantIds }),
      tenantCount: tenantIds.length,
      ...(realms.length > 0 && { realms }),
    }),
  });
};
